import React, { useContext, useEffect, useState } from "react";
import AdminModalContext from "../context/AdminModalContext";
import AuthContext from "../context/AuthContext";
import useAxios from "../utils/useAxios";
import { MONTH_LABELS } from "./WorkingDaysMasterModal";

// 稼働日数マスタの未設定年度は、Working Days Master モーダルを開いて該当年度を
// 選ばない限り気づけず、前年値のフォールバックのまま数か月運用される事故に
// つながる。管理者にはヘッダー常設の赤文字テキストで本年度の未設定を知らせる
// （モーダル内の枠色表示はそのまま残す）。
//
// あわせて、日本側の稼働日数に祝日が反映されていない可能性がある月も警告する。
// 日本側は「土日祝を除いた日数」を年初に手入力する運用だが、初期値は土日のみを
// 除いた値のため、入力前は上限（平日数 − 確実に存在する祝日の数）を超えたままに
// なる。判定は backend の constants.FIXED_PUBLIC_HOLIDAYS_BY_MONTH を参照。
const ALERT_COLOR = "#cf1322";
// ヘッダーはスクロールしても上部に固定される。ブランドとメニューの間に置くと
// 中間幅で数十 px まで押し縮められ、1 語ずつ折り返してヘッダーが 200px 超に
// 伸びるため、ナビゲーションの行の下に独立した 1 行として並べる
const ALERT_STYLE = {
	color: ALERT_COLOR,
	fontSize: 12,
	order: 3,
	flex: "0 0 100%",
	paddingTop: 0,
	paddingBottom: 4,
};

// 年越し検知の周期。画面を開いたまま年をまたいでも、最長この間隔で新年度の
// 再判定が走る（PR #134 Codexレビュー P2: 12/31から開きっぱなしのタブが
// 1/1以降も前年を「current year」として判定し続ける問題への対応）
const YEAR_CHECK_INTERVAL_MS = 60 * 60 * 1000;

const WorkingDaysUnsetAlert = () => {
	const { user } = useContext(AuthContext);
	// 保存成功のたびに増えるカウンタ。モーダルで未設定月を解消した直後に
	// 再判定し、ページ再読み込みなしでアラートを消すために依存へ入れる
	const { workingDaysSavedCount } = useContext(AdminModalContext);
	const api = useAxios();
	// { year, unset, exceedsMonths } もしくは null（表示すべき警告がない）
	const [alertInfo, setAlertInfo] = useState(null);
	const [currentYear, setCurrentYear] = useState(() =>
		new Date().getFullYear(),
	);

	const isAdmin = Boolean(user && (user.isAdmin || user.is_superuser));
	const userId = user ? user.user_id : null;

	useEffect(() => {
		// 同一年のあいだは setState が同値のため再レンダー・再フェッチは起きない
		const timer = setInterval(() => {
			setCurrentYear(new Date().getFullYear());
		}, YEAR_CHECK_INTERVAL_MS);
		return () => clearInterval(timer);
	}, []);

	useEffect(() => {
		if (!isAdmin) {
			setAlertInfo(null);
			return undefined;
		}
		// ログアウト・ユーザー切替後に旧ユーザー宛の応答で表示を汚染しないための
		// クリーンアップフラグ（get_year は管理者専用 API のため一般ユーザーでは呼ばない）
		let cancelled = false;
		const year = currentYear;
		api
			.get("working-days-master/get_year/", { params: { year } })
			.then((response) => {
				if (cancelled) return;
				const hasUnsetMonth =
					response.data.japan.fallback_months.length > 0 ||
					response.data.cairo.fallback_months.length > 0;
				// 上限チェックは日本側のみ（カイロは暦日数が正値で土日祝の概念がない）。
				// 応答に無い場合は空として扱う
				const exceedsMonths = response.data.japan.exceeds_months || [];
				setAlertInfo(
					hasUnsetMonth || exceedsMonths.length > 0
						? { year, unset: hasUnsetMonth, exceedsMonths }
						: null,
				);
			})
			.catch(() => {
				// 受動的なインジケーターのため取得失敗時は表示しない（誤検知の赤字で
				// 混乱させない）。正の確認手段は Working Days Master モーダル側にある
				if (!cancelled) setAlertInfo(null);
			});
		return () => {
			cancelled = true;
		};
		// api は毎レンダー新インスタンスのため依存に含めない（WorkingDaysMasterModal と同じ理由）
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isAdmin, userId, workingDaysSavedCount, currentYear]);

	if (alertInfo === null) return null;
	const { year: alertYear, unset, exceedsMonths } = alertInfo;
	const exceedsLabel = exceedsMonths
		.map((month) => MONTH_LABELS[month - 1])
		.join(", ");

	return (
		<>
			{unset ? (
				<span
					className="navbar-text"
					style={ALERT_STYLE}
					data-testid="working-days-unset-alert"
				>
					Working days for the current year ({alertYear}) have not been set
				</span>
			) : null}
			{exceedsMonths.length > 0 ? (
				<span
					className="navbar-text"
					style={ALERT_STYLE}
					data-testid="working-days-exceeds-alert"
				>
					{`Japan working days for ${alertYear} may not exclude public holidays: ${exceedsLabel}`}
				</span>
			) : null}
		</>
	);
};

export default WorkingDaysUnsetAlert;
